import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, {FC, Fragment, useEffect, useState} from 'react'
import {Modal, Button, Tabs, Tab, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import { useParams } from 'react-router-dom'
import {toast} from 'react-toastify'
import {
  PlatformDataType,
  AvailableMapItem,
} from 'src/interfaces/Platform.interface'
import {getBulkProductLinkingFilesHistory, uploadBulkProductLinking} from 'src/redux/actions/uploadAction'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import { accessSessionTokenKeyName } from 'src/utils/constants'
import { formatDateLocal, formatTimeZone } from 'src/utils/TimeZoneDateFormat'

const BulkProductLinking: FC<{
  show: boolean
  handleClose: () => void
  id: string
  platformData: PlatformDataType | null
  available_map: AvailableMapItem
}> = ({show, handleClose, id, platformData, available_map}) => {
  const [bulkProductLinkingFile, setBulkProductLinkingFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [filetype, setFiletype] = useState('')
  const [selectedMapKey, setSelectedMapKey] = useState('')
  const [invalidFileType, setInvalidFileType] = useState(false)
  const dispatch = useAppDispatch()
  const params = useParams();
  const [selectedMapIndex, setSelectedMapIndex] = useState(0)
  const [bulkProductLinkingFilesHistory, setBulkProductLinkingFilesHistory] = useState<BulkImportFilesHistoryResponse>();
  const {fetchedUserAccountData } = useAppSelector(state=>state.userAccountData)
  


  interface BulkImportPlatformData {
    id: number;
    api_id: string;
    api_code: string;
    created_at: string | null;
    updated_at: string;
  }

  interface BulkImportFile {
    id: number;
    created_at: string;
    updated_at: string;
    fileUrl: string;
    fileDirectory: string;
    feature: string;
    identifier: string;
    message: string;
    sync_status: string;
    platform: number;
    platform_data: BulkImportPlatformData[] | null;
    user: number;
  }

  interface BulkImportFilesHistoryResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: {
      files: BulkImportFile[];
    };
  }

  const CommonColumn: FC<{rowData: string}> = ({rowData}) => {
    return (
      <>
        <p>{rowData}</p>
      </>
    )
  }

  const columns = [
    {
      name: 'Identifier',
      selector: (row) => row.identifier,
    },
    {
      name: 'Sync Status',
      selector: (row) => row.sync_status,
    },
    {
      name: (
        <>
          <div style={{display: "flex", alignItems:"center", justifyContent: "center", gap: "6px"}}>
            <span>Created At</span>{' '}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
              >
              <span style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </div>
        </>
      ),
      cell: (row) => {

        return (
          
              <CommonColumn
                rowData={
                  row?.created_at
                    ? formatDateLocal(row.created_at)
                    : ''
                }
              />
        );
      },
      // sortable: true,
      right: true,
      width: '160px',
      center: true,
    },
    {
          name: (
            <>
              <div style={{display: "flex", alignItems:"center", justifyContent: "center", gap: "6px"}}>
                Updated At{' '}
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                  >
                  <span style={{ cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </OverlayTrigger>
                </div>
            </>
          ),
          cell: (row) => {
    
            return (
                  <CommonColumn
                    rowData={
                      row?.updated_at
                        ? formatDateLocal(row.updated_at)
                        : ''
                    }
                  />
                
            );
          },
          right: true,
          width: '160px',
          center: true,
        },
    {
      name: 'Action',
      cell: (row) => 
      <a
        href={row.fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        download
      >
        <Button variant="primary" size="sm">
          Download
        </Button>
      </a>,
    },
  ];


  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile) {
      const allowedExtensions = ['csv']
      const fileName = selectedFile.name
      const fileExtension = fileName.split('.').pop()?.toLowerCase()

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        setInvalidFileType(true)
        setLoading(false)
        event.target.value = ''
        return
      }
      setBulkProductLinkingFile(selectedFile)
      setInvalidFileType(false)
    } else {
      console.error('No file selected.')
    }
  }

  const handleUploadClick = async () => {
    if (bulkProductLinkingFile instanceof File) {
      setLoading(true)
      const result = await dispatch(
        uploadBulkProductLinking({
          id: id,
          BulkProductLinkingFiles: bulkProductLinkingFile,
          type: filetype,
        })
      )
      if (result.payload?.success) {
        toast.success(result.payload?.success)
        setBulkProductLinkingFile(null)
        handleClose()
        setLoading(false)
        return
      }

      if (result.payload?.data?.error) {
        toast.warning(result.payload?.data?.error)
        setLoading(false)
        return
      }
    } else {
      toast.warning('Please choose a file to upload')
    }
  }

  const getBulkLinkingFilesHistory = async () => {
    try {
      setLoading(true);
      const result = await dispatch(
        getBulkProductLinkingFilesHistory({
          id: params?.id,
          user: fetchedUserAccountData?.id,
        })
      );

      if (result.payload?.results?.files?.length > 0) {
        setBulkProductLinkingFilesHistory(result.payload);
      }

      if (result.payload?.success) {
        toast.success(result.payload?.success);
        handleClose(); 
        setLoading(false);
        return;
      }

      if (result.payload?.data?.error) {
        toast.warning(result.payload?.data?.error);
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (err) {
      console.error("Error in fetching file history:", err);
      toast.error("Something went wrong");
      setLoading(false);
    }
  };


  useEffect(() => {
    getBulkLinkingFilesHistory();
  }, [show]);

  useEffect(() => {
    if (Array.isArray(available_map) && available_map.length > 0) {
      setFiletype(available_map[0].map_type)
      setSelectedMapKey(available_map[0].unique_key || available_map[0].map_type)
      setSelectedMapIndex(0)
    }
  }, [available_map])
const data =bulkProductLinkingFilesHistory?.results?.files.filter((file) => file.identifier
 === filetype) || []
  return (
    <Fragment>
      <style>
      {`
        .nav-tabs .nav-link.active {
          color:rgb(255, 255, 255) !important;
          border-color: #0d6efd #0d6efd #fff !important;
          background-color: #0d6efd !important;
        }
      `}
    </style>
      <Modal show={show} onHide={handleClose} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Bulk Product Linking</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Tabs to select map type */}
          {Array.isArray(available_map) && available_map.length > 0 && (
            <Tabs
              id='map-type-tabs'
              activeKey={selectedMapKey}
              onSelect={(selectedKey) => {
                const newIndex = available_map.findIndex(
                  (m) => (m.unique_key || m.map_type) === selectedKey
                )
                if (newIndex !== -1) {
                  setFiletype(available_map[newIndex].map_type)
                  setSelectedMapKey(available_map[newIndex].unique_key || available_map[newIndex].map_type)
                  setSelectedMapIndex(newIndex)
                }
              }}
              className='mb-4 tab-gap-style'
            >
              {available_map.map((option) => (
                <Tab
                  eventKey={option.unique_key || option.map_type}
                  title={option.label}
                  key={option.unique_key || option.map_type}
                />
              ))}
            </Tabs>
          )}
          <div className='mb-3'>
            <label className='form-label d-block mb-1' style={{fontSize: '12px'}}>
              Download template for {available_map?.[selectedMapIndex]?.label}:
              <a
                href={available_map?.[selectedMapIndex]?.sample_url}
                className='btn btn-link btn-sm'
                style={{marginLeft: '10px', fontSize: '12px'}}
                rel='noopener noreferrer'
                title={`Download`}
                download
              >
                <i className='bi bi-cloud-arrow-down text-primary fs-4'></i>
              </a>
            </label>
          </div>

          <div className='mb-3'>
            <label className='form-label d-block' style={{fontSize: '12px'}}>
              Upload your mapping file
            </label>
            <div className='d-flex align-items-center gap-3'>
              <label
                htmlFor='bulk-upload'
                className='btn btn-outline-primary btn-sm d-flex align-items-center'
                style={{cursor: 'pointer', border: '1px solid black'}}
              >
                <i className='bi bi-upload me-2'></i> Choose File
              </label>
              {bulkProductLinkingFile && (
                <span
                  className='text-truncate'
                  title={bulkProductLinkingFile.name}
                  style={{maxWidth: '60%'}}
                >
                  <i className='bi bi-file-earmark-text me-1'></i>
                  {bulkProductLinkingFile.name}
                </span>
              )}
              <Button
                variant='primary'
                size='sm'
                disabled={loading || bulkProductLinkingFile == null}
                onClick={handleUploadClick}
              >
                {loading ? (
                  <>
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                      className='me-2'
                    />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
            <input
              key={selectedMapKey + 'change'}
              id='bulk-upload'
              type='file'
              className='d-none'
              accept='.csv'
              onChange={handleFileChange}
            />
            <small className={`form-text ${invalidFileType ? 'text-danger' : 'text-muted'}`}>
              Accepted formats: .csv
            </small>
          </div>
          <h5 className='mb-3'>Upload History Logs</h5>
          <div style={{height: '40vh', overflowY: 'auto'}}>
            <DataTable
              title=''
              fixedHeader={true}
              data={data}
              columns={columns}
              persistTableHead={true}
              // selectableRows
              // paginationTotalRows={data.length}
              fixedHeaderScrollHeight='30vh'
              pagination
              // paginationServer
              customStyles={{
                table: {
                  style: {
                    // height: '30vh',
                  },
                },
                headCells: {
                  style: {fontWeight: 'bold', fontSize: '14px'},
                },
                cells: {
                  style: {
                    padding: '10px',
                    borderBottom: '1px solid gainsboro',
                  },
                },
              }}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant='secondary'
            disabled={loading}
            onClick={() => {
              setInvalidFileType(false)
              setBulkProductLinkingFile(null)
              handleClose()
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  )
}

export default BulkProductLinking;
